import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Crown, TrendingUp, DollarSign, Shield, Target, Plus,
  Calendar, ChevronRight, Award, Zap, ArrowUpRight, Download,
  Medal, Trophy, Flame, Users, Globe, Loader2
} from 'lucide-react';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { toast } from 'sonner';
import api from '@/services/api';

const tierConfig = {
  platinum: { color: 'bg-purple-100 text-purple-800 border-purple-300', icon: Crown, label: 'Platinum' },
  gold: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Trophy, label: 'Gold' },
  silver: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Medal, label: 'Silver' },
  bronze: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: Award, label: 'Bronze' },
};

const YourWins = () => {
  const [timeRange, setTimeRange] = useState('ltm');
  const [showAddWin, setShowAddWin] = useState(false);
  const [newWin, setNewWin] = useState({ title: '', amount: '', category: 'negotiation', description: '' });
  const [leaderboard, setLeaderboard] = useState({ individual: [], departments: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const { branding } = useBranding();

  const COLORS = ['#16a34a', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'];

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await api.get('/leaderboard/');
      setLeaderboard(res.data);
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWin = () => {
    if (!newWin.title || !newWin.amount) {
      toast.error('Please fill in title and amount');
      return;
    }
    toast.success('Win recorded!');
    setShowAddWin(false);
    setNewWin({ title: '', amount: '', category: 'negotiation', description: '' });
  };

  const topThree = leaderboard.individual.slice(0, 3);
  const totalSavings = leaderboard.individual.reduce((sum, p) => sum + (p.savings_reported || 0), 0);
  const totalActions = leaderboard.individual.reduce((sum, p) => sum + (p.actions_taken || 0), 0);

  if (loading) {
    return (
      <Layout title="Team Savings & Leaderboard" subtitle="Track your procurement team's victories">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
      </Layout>
    );
  }

  return (
    <Layout title="Team Savings & Leaderboard" subtitle="Track your procurement team's victories and value delivered">
      <div className="space-y-6" data-testid="your-wins-page">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-44" data-testid="wins-time-filter">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mtd">This Month</SelectItem>
              <SelectItem value="qtd">This Quarter</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="ltm">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto flex gap-2">
            <Button
              onClick={() => {
                const doc = createBrandedPDF('Team Savings', 'Leaderboard Report', branding);
                let y = doc._startY;
                y = addSectionTitle(doc, 'Summary', y);
                y = addMetricCards(doc, [
                  { label: 'Total Savings', value: `$${totalSavings.toLocaleString()}` },
                  { label: 'Total Actions', value: String(totalActions) },
                  { label: 'Team Members', value: String(leaderboard.individual.length) },
                ], y);
                y = addSectionTitle(doc, 'Individual Leaderboard', y);
                y = addTable(doc, ['Rank', 'Name', 'Department', 'Savings', 'Points', 'Tier'],
                  leaderboard.individual.map(p => [String(p.rank), p.name, p.department, `$${p.savings_reported?.toLocaleString()}`, String(p.points), p.tier]), y);
                downloadPDF(doc, 'team-leaderboard.pdf');
                toast.success('PDF downloaded');
              }}
              variant="outline" className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="wins-export-pdf"
            >
              <Download className="h-4 w-4 mr-2" /> Export PDF
            </Button>
            <Button onClick={() => setShowAddWin(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-win-btn">
              <Plus className="h-4 w-4 mr-2" /> Record a Win
            </Button>
          </div>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-600 to-emerald-700 border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-100">Total Team Savings</p>
                  <p className="text-2xl font-bold text-white">${totalSavings.toLocaleString()}</p>
                </div>
                <Crown className="h-10 w-10 text-green-300 opacity-60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Team Members</p>
                  <p className="text-2xl font-bold text-gray-900">{leaderboard.individual.length}</p>
                </div>
                <Users className="h-10 w-10 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Actions</p>
                  <p className="text-2xl font-bold text-gray-900">{totalActions}</p>
                </div>
                <Target className="h-10 w-10 text-amber-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Departments</p>
                  <p className="text-2xl font-bold text-gray-900">{leaderboard.departments.length}</p>
                </div>
                <Globe className="h-10 w-10 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Podium */}
        {topThree.length >= 3 && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Top Performers</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-end justify-center gap-4 pb-2">
                {[topThree[1], topThree[0], topThree[2]].map((person, idx) => {
                  const place = idx === 0 ? 2 : idx === 1 ? 1 : 3;
                  const height = place === 1 ? 'h-32' : place === 2 ? 'h-24' : 'h-20';
                  const bg = place === 1 ? 'bg-yellow-400' : place === 2 ? 'bg-gray-300' : 'bg-orange-300';
                  const tier = tierConfig[person.tier] || tierConfig.bronze;
                  return (
                    <div key={person.name} className="flex flex-col items-center" data-testid={`podium-${place}`}>
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2 border-2 border-gray-200">
                        <span className="text-lg font-bold text-gray-700">{person.name.charAt(0)}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">{person.name}</p>
                      <Badge className={`${tier.color} text-xs mb-2`}>{tier.label}</Badge>
                      <p className="text-xs text-gray-500 mb-1">{person.points} pts</p>
                      <div className={`${height} w-20 ${bg} rounded-t-lg flex items-center justify-center`}>
                        <span className="text-2xl font-bold text-white">#{place}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="individual" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="individual">Individual</TabsTrigger>
            <TabsTrigger value="department">Department</TabsTrigger>
            <TabsTrigger value="country">Country</TabsTrigger>
          </TabsList>

          {/* Individual Leaderboard */}
          <TabsContent value="individual" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rank</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Savings</TableHead>
                      <TableHead>Actions</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Streak</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard.individual.map(person => {
                      const tier = tierConfig[person.tier] || tierConfig.bronze;
                      return (
                        <TableRow key={person.name} data-testid={`leaderboard-row-${person.rank}`}>
                          <TableCell className="font-bold text-lg">{person.rank <= 3 ? ['', '1st', '2nd', '3rd'][person.rank] : `#${person.rank}`}</TableCell>
                          <TableCell className="font-medium">{person.name}</TableCell>
                          <TableCell><Badge variant="outline">{person.department}</Badge></TableCell>
                          <TableCell className="font-semibold text-green-600">${person.savings_reported?.toLocaleString()}</TableCell>
                          <TableCell>{person.actions_taken}</TableCell>
                          <TableCell className="font-bold">{person.points}</TableCell>
                          <TableCell><Badge className={`${tier.color} text-xs`}>{tier.label}</Badge></TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1 text-orange-500">
                              <Flame className="h-3 w-3" />{person.streak}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Department Leaderboard */}
          <TabsContent value="department" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Department</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Total Savings</TableHead>
                      <TableHead>Total Points</TableHead>
                      <TableHead>Top Performer</TableHead>
                      <TableHead>Tier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard.departments.map(dept => {
                      const tier = tierConfig[dept.tier] || tierConfig.bronze;
                      return (
                        <TableRow key={dept.department}>
                          <TableCell className="font-medium">{dept.department}</TableCell>
                          <TableCell>{dept.members}</TableCell>
                          <TableCell className="font-semibold text-green-600">${dept.total_savings?.toLocaleString()}</TableCell>
                          <TableCell className="font-bold">{dept.total_points}</TableCell>
                          <TableCell>{dept.top_performer}</TableCell>
                          <TableCell><Badge className={`${tier.color} text-xs`}>{tier.label}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Country Leaderboard */}
          <TabsContent value="country" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {leaderboard.countries.map((c, idx) => (
                    <div key={c.country} className="p-4 border rounded-lg bg-gray-50 flex items-center gap-4" data-testid={`country-card-${c.country}`}>
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{c.country}</p>
                        <p className="text-sm text-gray-500">{c.members} members</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">${c.total_savings?.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{c.total_points} pts</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Win Dialog */}
        <Dialog open={showAddWin} onOpenChange={setShowAddWin}>
          <DialogContent>
            <DialogHeader><DialogTitle>Record a Win</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input value={newWin.title} onChange={e => setNewWin({ ...newWin, title: e.target.value })} placeholder="e.g., Renegotiated MLCC pricing" className="mt-1" />
              </div>
              <div>
                <Label>Savings Amount ($) *</Label>
                <Input type="number" value={newWin.amount} onChange={e => setNewWin({ ...newWin, amount: e.target.value })} placeholder="e.g., 5000" className="mt-1" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newWin.category} onValueChange={v => setNewWin({ ...newWin, category: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="negotiation">Negotiation</SelectItem>
                    <SelectItem value="alternate">Alternate Part</SelectItem>
                    <SelectItem value="risk">Risk Mitigation</SelectItem>
                    <SelectItem value="volume">Volume Optimization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newWin.description} onChange={e => setNewWin({ ...newWin, description: e.target.value })} placeholder="Describe what you did..." className="mt-1" rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddWin(false)}>Cancel</Button>
              <Button onClick={handleAddWin} className="bg-green-600 hover:bg-green-700 text-white">Save Win</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default YourWins;
